import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ExternalLink, MapPin, Search, ShieldCheck, Star, Stethoscope } from 'lucide-react';
import { matchGoogleFacilities, searchGoogleCarePlaces } from '../lib/api';
import { loadFindCareLibraries } from '../lib/googleMaps';
import './FindCarePage.css';

type SearchCenter = google.maps.LatLngLiteral;
type CareCategory = 'all' | 'veterinary' | 'boarding' | 'pet-care';
type ActapetStatus = 'verified' | 'unverified' | 'external';
type CareResult = {
  id: string;
  name: string;
  address: string;
  type: string;
  location: google.maps.LatLngLiteral;
  mapsUrl: string;
  rating: number | null;
  ratingCount: number | null;
  businessStatus: string | null;
  actapetStatus: ActapetStatus;
};

const defaultCenter: SearchCenter = { lat: 39.8283, lng: -98.5795 };
const verificationRank: Record<ActapetStatus, number> = { verified: 0, unverified: 1, external: 2 };
const markerColors: Record<ActapetStatus, string> = { verified: '#e8876b', unverified: '#568d76', external: '#505858' };
function addressPart(components: { longText?: string; types?: string[] }[], type: string) {
  return components.find((component) => component.types?.includes(type))?.longText ?? '';
}

function mapPinIcon(color: string): google.maps.Icon {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48"><path d="M18 1C8.6 1 1 8.6 1 18c0 12.6 17 29 17 29s17-16.4 17-29C35 8.6 27.4 1 18 1Z" fill="' + color + '" stroke="#fff" stroke-width="2"/><circle cx="18" cy="17" r="6" fill="#fff"/></svg>';
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(36, 48),
    anchor: new google.maps.Point(18, 47),
  };
}

export default function FindCarePage() {
  const { getAccessTokenSilently } = useAuth0();
  const mapElement = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const markers = useRef<google.maps.Marker[]>([]);
  const placesLibrary = useRef<google.maps.PlacesLibrary | null>(null);
  const placesSession = useRef<google.maps.places.AutocompleteSessionToken>();
  const searchSequence = useRef(0);
  const [mapReady, setMapReady] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [searchCenter, setSearchCenter] = useState<SearchCenter | null>(null);
  const [searchLabel, setSearchLabel] = useState('');
  const [category, setCategory] = useState<CareCategory>('all');
  const [results, setResults] = useState<CareResult[]>([]);
  const [selectedResultId, setSelectedResultId] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void loadFindCareLibraries().then(({ maps: library, places }) => {
      if (!active || !mapElement.current) return;
      placesLibrary.current = places;
      map.current = new library.Map(mapElement.current, {
        center: defaultCenter,
        zoom: 4,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
      });
      setMapReady(true);
    }).catch((loadError: unknown) => {
      if (active) setError(loadError instanceof Error ? loadError.message : 'Unable to load Google Maps.');
    });
    return () => {
      active = false;
      markers.current.forEach((marker) => marker.setMap(null));
      markers.current = [];
      map.current = null;
    };
  }, []);

  useEffect(() => {
    const query = locationQuery.trim();
    const places = placesLibrary.current;
    if (!places || query.length < 3 || searchCenter) {
      setLocationSuggestions([]);
      return;
    }
    const sequence = ++searchSequence.current;
    const timeout = window.setTimeout(() => {
      placesSession.current ??= new places.AutocompleteSessionToken();
      void places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: query, sessionToken: placesSession.current })
        .then(({ suggestions }) => {
          if (searchSequence.current === sequence) setLocationSuggestions(suggestions.filter((suggestion) => suggestion.placePrediction));
        })
        .catch(() => {
          if (searchSequence.current === sequence) setError('Unable to search for that location.');
        });
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [locationQuery, mapReady, searchCenter]);

  const selectLocation = async (prediction: google.maps.places.PlacePrediction) => {
    try {
      searchSequence.current += 1;
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress', 'location'] });
      if (!place.location) throw new Error('This location has no map coordinates.');
      const center = place.location.toJSON();
      setSearchCenter(center);
      setSearchLabel(place.formattedAddress || prediction.text.toString());
      setLocationQuery(place.formattedAddress || prediction.text.toString());
      setLocationSuggestions([]);
      placesSession.current = undefined;
      map.current?.panTo(center);
      map.current?.setZoom(12);
      setError('');
    } catch (selectionError) {
      setError(selectionError instanceof Error ? selectionError.message : 'Unable to use this location.');
    }
  };

  const findCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Location is not available in this browser. Search for a city or ZIP code instead.');
      return;
    }
    setError('');
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      const center = { lat: coords.latitude, lng: coords.longitude };
      setSearchCenter(center);
      setSearchLabel('Current location');
      setLocationQuery('Current location');
      setLocationSuggestions([]);
      map.current?.panTo(center);
      map.current?.setZoom(12);
      void searchNearby(center, 'Current location', category);
    }, () => setError('Unable to access your location. Search for a city or ZIP code instead.'), { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 });
  };

  const searchNearby = async (center = searchCenter, label = searchLabel, selectedCategory = category) => {
    if (!center || !placesLibrary.current) {
      setError('Choose a city, address, or use your location before searching.');
      return;
    }
    const sequence = ++searchSequence.current;
    setSearching(true);
    setError('');
    setSelectedResultId('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      const { places } = await searchGoogleCarePlaces(token, {
        latitude: center.lat,
        longitude: center.lng,
        location_label: label,
        category: selectedCategory,
      });
      if (searchSequence.current !== sequence) return;
      const candidates = places.flatMap((place) => {
        const latitude = place.location?.latitude;
        const longitude = place.location?.longitude;
        const name = place.displayName?.text?.trim();
        if (!place.id || typeof latitude !== 'number' || typeof longitude !== 'number' || !name) return [];
        const location = { lat: latitude, lng: longitude };
        const components = place.addressComponents ?? [];
        const streetNumber = addressPart(components, 'street_number');
        const route = addressPart(components, 'route');
        return [{
          place_id: place.id,
          name,
          address: place.formattedAddress || '',
          address_line1: [streetNumber, route].filter(Boolean).join(' '),
          city: addressPart(components, 'locality') || addressPart(components, 'postal_town'),
          region: addressPart(components, 'administrative_area_level_1'),
          postal_code: addressPart(components, 'postal_code'),
          place,
          location,
        }];
      });
      const matchedStatuses = new Map<string, 'verified' | 'unverified'>();
      if (candidates.length) {
        const matched = await matchGoogleFacilities(token, candidates.map(({ place_id, name, address, address_line1, city, region, postal_code }) => ({ place_id, name, address, address_line1, city, region, postal_code })));
        matched.matches.forEach(({ place_id, status }) => matchedStatuses.set(place_id, status));
      }
      if (searchSequence.current !== sequence) return;
      const nextResults: CareResult[] = candidates.map(({ place, location, place_id, name, address }) => ({
        id: place_id,
        name,
        address,
        type: place.primaryTypeDisplayName?.text || 'Pet care',
        location,
        mapsUrl: place.googleMapsUri || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name} ${address}`)}`,
        rating: place.rating ?? null,
        ratingCount: place.userRatingCount ?? null,
        businessStatus: place.businessStatus ?? null,
        actapetStatus: matchedStatuses.get(place_id) ?? 'external',
      }));
      nextResults.sort((left, right) => verificationRank[left.actapetStatus] - verificationRank[right.actapetStatus]);
      const displayedResults = nextResults.slice(0, 60);
      setResults(displayedResults);
      markers.current.forEach((marker) => marker.setMap(null));
      markers.current = [];
      map.current?.panTo(center);
      map.current?.setZoom(11);
      displayedResults.forEach((result) => {
        const marker = new google.maps.Marker({ map: map.current, position: result.location, title: result.name, icon: mapPinIcon(markerColors[result.actapetStatus]) });
        marker.addListener('click', () => setSelectedResultId(result.id));
        markers.current.push(marker);
      });
      setSearchLabel(label);
    } catch (searchError) {
      if (searchSequence.current === sequence) setError(searchError instanceof Error ? searchError.message : 'Unable to find nearby pet care.');
    } finally {
      if (searchSequence.current === sequence) setSearching(false);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!searchCenter) {
      setError('Select a suggested location before searching.');
      return;
    }
    void searchNearby(searchCenter, searchLabel, category);
  };

  return <>
    <div className="facility-page-heading"><div><p className="eyebrow">Care near you</p><h1>Find care</h1><p>Search nearby veterinary, boarding, and pet-care facilities.</p></div><button type="button" className="button button-secondary" onClick={findCurrentLocation}><MapPin size={17} /> Use my location</button></div>
    {error && <p className="form-error facility-error" role="alert">{error}</p>}
    <div className="finder-layout find-care-layout">
      <section className="map-panel find-care-map-panel">
        <form className="map-toolbar find-care-toolbar" onSubmit={handleSearchSubmit}>
          <div className="google-address-search find-care-location-search">
            <input aria-label="Search by city or address" aria-expanded={locationSuggestions.length > 0} aria-controls="find-care-location-suggestions" placeholder="City, address, or ZIP code" value={locationQuery === 'Current location' ? searchLabel : locationQuery} onChange={(event) => { setLocationQuery(event.target.value); setSearchCenter(null); setSearchLabel(''); setLocationSuggestions([]); setError(''); }} onKeyDown={(event) => { if (event.key === 'Escape') setLocationSuggestions([]); }} />
            {locationSuggestions.length > 0 && <div id="find-care-location-suggestions" className="google-address-suggestions" role="listbox" aria-label="Location suggestions">{locationSuggestions.map((suggestion) => { const prediction = suggestion.placePrediction; return prediction && <button type="button" role="option" className="google-address-suggestion" key={prediction.placeId} onClick={() => void selectLocation(prediction)}><strong>{prediction.mainText?.toString() || prediction.text.toString()}</strong>{prediction.secondaryText && <span>{prediction.secondaryText.toString()}</span>}</button>; })}</div>}
          </div>
          <select className="find-care-category" aria-label="Pet care category" value={category} onChange={(event) => setCategory(event.target.value as CareCategory)}><option value="all">All pet care</option><option value="veterinary">Veterinary</option><option value="boarding">Boarding</option><option value="pet-care">Pet care</option></select>
          <button type="submit" className="icon-button" aria-label="Search nearby facilities" title="Search nearby" disabled={searching || !mapReady}>{searching ? <span className="find-care-spinner" /> : <Search size={18} />}</button>
        </form>
        <div className="find-care-map-wrap"><div className="find-care-map" ref={mapElement} aria-label="Google map of nearby pet care facilities" />{!mapReady && <div className="find-care-map-status">Loading Google Maps...</div>}</div>
      </section>
      <section className="panel find-care-results">
        <div className="list-heading"><div><h2>Nearby facilities</h2><p>{searchLabel || 'Choose a location to search'}</p></div><span>{results.length} results</span></div>
        {results.length === 0 ? <p className="find-care-empty">{searching ? 'Searching Google Maps...' : 'Choose a city or use your location to find nearby care.'}</p> : <div className="find-care-result-list">{results.map((result) => <article className={`find-care-result ${result.actapetStatus !== 'external' ? `actapet-${result.actapetStatus}` : ''} ${selectedResultId === result.id ? 'selected' : ''}`} key={result.id} onMouseEnter={() => setSelectedResultId(result.id)}><div className="find-care-result-top"><div className="find-care-result-icon"><Stethoscope size={17} /></div><div className="find-care-result-name"><h3>{result.name}</h3><span>{result.type}</span></div>{result.actapetStatus !== 'external' && <span className={`actapet-${result.actapetStatus}-badge`}><ShieldCheck size={12} /> Actapet {result.actapetStatus === 'verified' ? 'Verified' : 'Unverified'}</span>}</div><p className="find-care-result-address">{result.address}</p><div className="find-care-result-footer">{result.rating !== null && <span className="find-care-rating"><Star size={13} /> {result.rating.toFixed(1)}{result.ratingCount !== null ? ` (${result.ratingCount})` : ''}</span>}{result.businessStatus === 'CLOSED_PERMANENTLY' ? <span className="find-care-closed">Permanently closed</span> : <span>{searchLabel}</span>}<a href={result.mapsUrl} target="_blank" rel="noreferrer" aria-label={`Open ${result.name} in Google Maps`}><ExternalLink size={14} /></a></div></article>)}</div>}
        <div className="find-care-attribution">Results and map provided by Google Maps</div>
      </section>
    </div>
  </>;
}
